import {
  ChatTransportError,
  clientMessageIdOf,
  type ChatConversation,
  type ChatConversationStatus,
  type ChatMessage,
  type ChatMessageListener,
  type ChatMessagePage,
  type ChatSendCapabilities,
  type ChatTransport,
  type OutgoingChatMessage,
  type Unsubscribe,
} from './chatTransport';

/**
 * In-memory, event-driven chat transport.
 *
 * This is the injection point the seam exists for: seed it with conversations
 * and messages, drop it into the chat transport slot, and the real chat render +
 * realtime pipeline runs against it with no live connection, token, or backend.
 *
 * Event delivery is synchronous — `receiveMessage`/`updateMessage` invoke
 * listeners before returning, so a test awaits a microtask, not a debounce
 * timer. Tests remain deterministic without fake timers.
 */

export interface FakeConversationSeed {
  id: string;
  messages?: ChatMessage[];
  attributes?: unknown;
  lastReadIndex?: number | null;
  status?: ChatConversationStatus;
  /** Whose sends this conversation records. */
  viewerId?: string;
  /** Answers a repeated `clientMessageId` with the message it already sent, as Primum's store will. */
  idempotentSend?: boolean;
}

export class FakeChatConversation implements ChatConversation {
  readonly id: string;

  attributes: unknown;

  lastReadIndex: number | null;

  status?: ChatConversationStatus;

  readonly capabilities: ChatSendCapabilities;

  private readonly viewerId: string;

  /**
   * Ascending by index. `protected` so the
   * polling transport subclass can hydrate and diff against it while reusing this
   * class's paging and event-emitter machinery unchanged.
   */
  protected messages: ChatMessage[];

  private addedListeners = new Set<ChatMessageListener>();

  private updatedListeners = new Set<ChatMessageListener>();

  constructor(seed: FakeConversationSeed) {
    this.id = seed.id;

    this.attributes = seed.attributes ?? {};

    this.lastReadIndex = seed.lastReadIndex ?? null;

    this.status = seed.status;

    this.viewerId = seed.viewerId ?? 'viewer';

    this.capabilities = { idempotentSend: seed.idempotentSend ?? false };

    this.messages = [...(seed.messages ?? [])].sort((first, second) => first.index - second.index);
  }

  get lastMessage() {
    const last = this.messages.at(-1);

    if (!last) {
      return undefined;
    }

    return { index: last.index, createdAt: last.createdAt ?? undefined };
  }

  getMessages(pageSize = 30, anchor?: number): Promise<ChatMessagePage> {
    const eligible =
      anchor === undefined
        ? this.messages
        : this.messages.filter((message) => message.index <= anchor);

    const start = Math.max(0, eligible.length - pageSize);

    return Promise.resolve({
      items: eligible.slice(start),
      hasPrevPage: start > 0,
    });
  }

  markAllRead(): Promise<void> {
    const last = this.messages.at(-1);

    this.lastReadIndex = last ? last.index : this.lastReadIndex;

    return Promise.resolve();
  }

  /**
   * Stores the message and delivers it as `messageAdded`, as the provider
   * would once the send lands. Idempotent only when the seed says so.
   */
  send(message: OutgoingChatMessage): Promise<ChatMessage | undefined> {
    const repeated = this.capabilities.idempotentSend
      ? this.messages.find((stored) => clientMessageIdOf(stored) === message.clientMessageId)
      : undefined;

    if (repeated) {
      return Promise.resolve(repeated);
    }

    const stored: ChatMessage = {
      id: `IM-${message.clientMessageId}-${this.messages.length}`,
      index: (this.messages.at(-1)?.index ?? -1) + 1,
      author: this.viewerId,
      body: message.body,
      attributes: {
        ...(message.data ?? {}),
        ...(message.files?.length ? { files: message.files } : {}),
        clientMessageId: message.clientMessageId,
      },
      createdAt: new Date(),
      updatedAt: null,
    };

    this.receiveMessage(stored);

    return Promise.resolve(stored);
  }

  onMessageAdded(listener: ChatMessageListener): Unsubscribe {
    this.addedListeners.add(listener);

    return () => {
      this.addedListeners.delete(listener);
    };
  }

  onMessageUpdated(listener: ChatMessageListener): Unsubscribe {
    this.updatedListeners.add(listener);

    return () => {
      this.updatedListeners.delete(listener);
    };
  }

  /** Test driver: deliver a new inbound message and fire `messageAdded`. */
  receiveMessage(message: ChatMessage): void {
    this.messages = [...this.messages, message].sort((first, second) => first.index - second.index);

    this.addedListeners.forEach((listener) => listener(message));
  }

  /** Test driver: patch an existing message by id and fire `messageUpdated`. */
  updateMessage(id: string, changes: Partial<ChatMessage>): void {
    let updated: ChatMessage | undefined;

    this.messages = this.messages.map((message) => {
      if (message.id !== id) {
        return message;
      }

      updated = { ...message, ...changes };

      return updated;
    });

    if (updated) {
      this.updatedListeners.forEach((listener) => listener(updated as ChatMessage));
    }
  }
}

export class FakeChatTransport implements ChatTransport {
  private conversations = new Map<string, FakeChatConversation>();

  constructor(seeds: FakeConversationSeed[] = []) {
    for (const seed of seeds) {
      this.conversations.set(seed.id, new FakeChatConversation(seed));
    }
  }

  getConversation(conversationId: string): Promise<FakeChatConversation> {
    const conversation = this.conversations.get(conversationId);

    if (!conversation) {
      return Promise.reject(
        new ChatTransportError(
          'not_found',
          `FakeChatTransport: no conversation with id ${conversationId}`
        )
      );
    }

    return Promise.resolve(conversation);
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  /** Reach a seeded conversation directly to drive events from a test. */
  conversation(id: string): FakeChatConversation {
    const conversation = this.conversations.get(id);

    if (!conversation) {
      throw new Error(`FakeChatTransport: no conversation with id ${id}`);
    }

    return conversation;
  }
}
